<template>
    <b-modal
        ref="modal"
        :title="params.title"
        header-bg-variant="secondary"
        header-text-variant="light"
        footer-bg-variant="light"
        cancel-variant="danger"
        cancel-title="Cancelar"
        :ok-title="params.okTitle"
        ok-variant="secondary"
        :busy="loading"
        scrollable
        centered
        no-close-on-esc
        no-close-on-backdrop
        hide-header-close
        no-fade
        @ok="confirm"
    >
        <template v-slot:default="{ visible }">
            <b-form v-if="visible" novalidate>
                <b-form-group
                    id="username_group_id"
                    label="Nome de usuário"
                    label-for="username_id"
                    description="Chave de acesso ao sistema"
                    :state="errors.username.state"
                    :invalid-feedback="errors.username.message"
                >
                    <b-form-input
                        id="username_id"
                        v-model="data.username"
                        required
                        placeholder="Nome de usuário..."
                        :disabled="loading"
                        :state="errors.username.state"
                        @change="errors.username.state = null"
                    ></b-form-input>
                </b-form-group>
                <b-form-group
                    v-if="action === 'create' || changePassword"
                    id="password1_group_id"
                    label="Senha"
                    label-for="password1_id"
                    :state="errors.password1.state"
                    :invalid-feedback="errors.password1.message"
                >
                    <template v-slot:description>
                        <p
                            v-for="(tip, index) in passwordTips"
                            :key="index"
                            class="text-secondary mb-0"
                        >
                            {{ tip }}
                        </p>
                    </template>
                    <b-form-input
                        id="password1_id"
                        v-model="data.password1"
                        type="password"
                        :state="errors.password1.state"
                        required
                        placeholder="Senha..."
                        :disabled="loading"
                        @change="errors.password1.state = null"
                    ></b-form-input>
                </b-form-group>
                <b-form-group
                    v-if="action === 'create' || changePassword"
                    id="password2_group_id"
                    label="Senha (novamente)"
                    label-for="password2_id"
                    :state="errors.password2.state"
                    :invalid-feedback="errors.password2.message"
                    description="Digite a mesma senha informada anteriormente, para verificação."
                >
                    <b-form-input
                        id="password2_id"
                        v-model="data.password2"
                        type="password"
                        placeholder="Senha..."
                        :state="errors.password2.state"
                        :disabled="loading"
                        @change="errors.password2.state = null"
                    ></b-form-input>
                </b-form-group>
                <b-form-group v-if="action === 'edit'">
                    <b-button
                        size="sm"
                        variant="link"
                        :disabled="loading"
                        :class="`text-decoration-none${
                            changePassword ? ' text-danger' : ''
                        }`"
                        @click="changePassword = !changePassword"
                    >
                        {{
                            changePassword
                                ? "Cancelar alteração de senha"
                                : "Alterar senha"
                        }}
                    </b-button>
                </b-form-group>
                <b-form-group
                    id="email_group_id"
                    label="E-mail"
                    label-for="email_id"
                    :state="errors.email.state"
                    :invalid-feedback="errors.email.message"
                >
                    <b-form-input
                        id="email_id"
                        v-model="data.email"
                        type="email"
                        placeholder="E-mail..."
                        :disabled="loading"
                        :state="errors.email.state"
                        @change="errors.email.state = null"
                    ></b-form-input>
                </b-form-group>
                <b-form-group
                    id="first_name_group_id"
                    label="Nome"
                    label-for="first_name_id"
                    :state="errors.first_name.state"
                    :invalid-feedback="errors.first_name.message"
                >
                    <b-form-input
                        id="first_name_id"
                        v-model="data.first_name"
                        :disabled="loading"
                        placeholder="Nome..."
                        :state="errors.first_name.state"
                        @change="errors.first_name.state = null"
                    ></b-form-input>
                </b-form-group>
                <b-form-group
                    id="last_name_group_id"
                    label="Sobrenome"
                    label-for="last_name_id"
                    :state="errors.last_name.state"
                    :invalid-feedback="errors.last_name.message"
                >
                    <b-form-input
                        id="last_name_id"
                        v-model="data.last_name"
                        :disabled="loading"
                        placeholder="Sobrenome..."
                        :state="errors.last_name.state"
                        @change="errors.last_name.state = null"
                    ></b-form-input>
                </b-form-group>
                <b-form-group
                    id="is_active_group_id"
                    label-for="is_active_id"
                    label="Usuário ativo"
                    description="Define se o usuário poderá acessar o sistema."
                >
                    <b-form-checkbox
                        id="is_active_id"
                        v-model="data.is_active"
                        :disabled="loading"
                        switch
                    >
                        {{ data.is_active ? "Sim" : "Não" }}
                    </b-form-checkbox>
                </b-form-group>
                <b-form-group
                    id="groups_group_id"
                    label-for="groups_id"
                    label="Permissões de acesso"
                    :state="errors.groups.state"
                    :invalid-feedback="errors.groups.message"
                    description="Define quais barragens o usuário terá acesso dentro do sistema."
                >
                    <b-form-checkbox-group
                        id="groups_id"
                        v-model="data.groups"
                        :state="errors.groups.state"
                        :options="groups"
                        :disabled="loading"
                        switches
                        stacked
                        @change="errors.groups.state = null"
                    />
                </b-form-group>
                <b-form-group
                    v-if="data.groups.includes(FozChapeco.group)"
                    id="fz_user_permissions_group_id"
                    label-for="fz_user_permissions_id"
                    :label="`${FozChapeco.group} - Permissões especiais`"
                    :state="errors.user_permissions.state"
                    :invalid-feedback="errors.user_permissions.message"
                    :description="`Define permissões especiais que o usuário pode ter para ${FozChapeco.group}.`"
                >
                    <b-form-checkbox-group
                        id="fz_user_permissions_id"
                        v-model="data.user_permissions"
                        :state="errors.user_permissions.state"
                        :options="FozChapeco.user_permissions"
                        :disabled="loading"
                        switches
                        stacked
                        @change="errors.user_permissions.state = null"
                    />
                </b-form-group>
                <b-form-group
                    v-if="data.groups.includes(CamposNovos.group)"
                    id="cn_user_permissions_group_id"
                    label-for="cn_user_permissions_id"
                    :label="`${CamposNovos.group} - Permissões especiais`"
                    :state="errors.user_permissions.state"
                    :invalid-feedback="errors.user_permissions.message"
                    :description="`Define permissões especiais que o usuário pode ter para ${CamposNovos.group}.`"
                >
                    <b-form-checkbox-group
                        id="cn_user_permissions_id"
                        v-model="data.user_permissions"
                        :state="errors.user_permissions.state"
                        :options="CamposNovos.user_permissions"
                        :disabled="loading"
                        switches
                        stacked
                        @change="errors.user_permissions.state = null"
                    />
                </b-form-group>
                <b-form-group
                    v-if="data.groups.includes(Julho14.group)"
                    id="j14_user_permissions_group_id"
                    label-for="j14_user_permissions_id"
                    :label="`${Julho14.group} - Permissões especiais`"
                    :state="errors.user_permissions.state"
                    :invalid-feedback="errors.user_permissions.message"
                    :description="`Define permissões especiais que o usuário pode ter para ${Julho14.group}.`"
                >
                    <b-form-checkbox-group
                        id="j14_user_permissions_id"
                        v-model="data.user_permissions"
                        :state="errors.user_permissions.state"
                        :options="Julho14.user_permissions"
                        :disabled="loading"
                        switches
                        stacked
                        @change="errors.user_permissions.state = null"
                    />
                </b-form-group>
                <b-form-group
                    id="general_permissions_group_id"
                    label-for="general_permissions_id"
                    label="Permissões gerais"
                    :state="errors.user_permissions.state"
                    :invalid-feedback="errors.user_permissions.message"
                >
                    <b-form-checkbox-group
                        id="general_permissions_id"
                        v-model="data.user_permissions"
                        :state="errors.user_permissions.state"
                        :options="General.user_permissions"
                        :disabled="loading"
                        switches
                        stacked
                        @change="errors.user_permissions.state = null"
                    />
                </b-form-group>
            </b-form>
        </template>
    </b-modal>
</template>

<script>
import { mapActions } from "vuex";
import * as camposNovos from "@/modules/campos_novos/settings";
import * as fozChapeco from "@/modules/foz_chapeco/settings";
import * as julho14 from "@/modules/julho_14/settings";
import * as core from "@/modules/core/settings";

const mapPerm = perm => ({
    value: perm[0],
    text: perm[1]
});

const mapInfo = dam => ({
    group: dam.GROUP_NAME,
    user_permissions: dam.SPECIAL_PERMISSIONS.map(mapPerm)
});

export default {
    name: "UsersEditor",
    props: {
        user: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            data: {},
            changePassword: false,
            loading: false,
            errors: {}
        };
    },
    computed: {
        FozChapeco() {
            return mapInfo(fozChapeco);
        },
        CamposNovos() {
            return mapInfo(camposNovos);
        },
        Julho14() {
            return mapInfo(julho14);
        },
        General() {
            return {
                user_permissions: core.SPECIAL_PERMISSIONS.map(mapPerm)
            };
        },
        action() {
            return this.data.id ? "edit" : "create";
        },
        params() {
            const params = {};
            if (this.action === "edit") {
                params.title = "Editar usuário";
                params.okTitle = this.loading
                    ? "Salvando usuário..."
                    : "Salvar";
            } else {
                params.title = "Criar usuário";
                params.okTitle = this.loading ? "Criando usuário..." : "Criar";
            }
            return params;
        }
    },
    created() {
        this.passwordTips = [
            "A senha não pode ser muito parecida com o resto das suas informações pessoais.",
            "A senha precisa conter pelo menos 8 caracteres.",
            "A senha não pode ser uma senha comumente utilizada.",
            "A senha não pode ser inteiramente numérica."
        ];
        this.groups = [
            fozChapeco.GROUP_NAME,
            camposNovos.GROUP_NAME,
            julho14.GROUP_NAME
        ].map(item => ({
            text: item,
            value: item
        }));
    },
    methods: {
        ...mapActions("settings/users", ["updateUser", "createUser"]),
        show() {
            this.errors = {};
            if (this.user) {
                this.changePassword = false;
                this.data = {
                    ...this.user,
                    password1: "",
                    password2: ""
                };
            } else {
                this.data = {
                    is_active: true,
                    username: "",
                    password1: "",
                    password2: "",
                    email: "",
                    first_name: "",
                    last_name: "",
                    groups: [],
                    user_permissions: []
                };
            }
            this.errors = this.buildErrors();
            this.$refs.modal.show();
        },
        buildErrors(errors = {}) {
            const data = {};

            for (const key of Object.keys(this.data)) {
                if (key !== "id") {
                    data[key] = {
                        state: null,
                        message: ""
                    };
                }
            }

            for (const [field, messages] of Object.entries(errors)) {
                data[field] = {
                    state: false,
                    message: messages[0]
                };
            }

            if ("password" in errors) {
                data.password2.state = false;
                data.password2.message = errors.password[0];
            }

            return data;
        },
        validatePassword() {
            if (this.action === "edit" && !this.changePassword) {
                return true;
            }
            const { password1, password2 } = this.data;
            if (password1 !== password2) {
                this.errors.password2.state = false;
                this.errors.password2.message =
                    "As senhas informadas não conferem.";
                this.errors.password1.state = false;
                this.errors.password1.message =
                    "As senhas informadas não conferem.";
                return false;
            }
            this.data.password = password2;
            return true;
        },
        async confirm(event) {
            event.preventDefault();
            if (this.validatePassword()) {
                this.loading = true;
                let message = "";
                try {
                    if (this.action === "create") {
                        await this.createUser(this.data);
                        message = "criado";
                    } else {
                        await this.updateUser(this.data);
                        message = "atualizado";
                    }
                    this.$bvToast.toast(
                        `Usuário ${this.data.username} ${message} com sucesso.`,
                        {
                            title: "Aviso",
                            variant: "info",
                            noCloseButton: true
                        }
                    );
                    this.$refs.modal.hide();
                } catch ({ response }) {
                    if (response.status === 400) {
                        this.errors = this.buildErrors(response.data);
                    } else {
                        this.$bvToast.toast(
                            "Erro ao executar operação, tente novamente mais tarde ou informe ao administrador do sistema.",
                            {
                                title: "Aviso",
                                variant: "danger",
                                noCloseButton: true
                            }
                        );
                    }
                } finally {
                    this.loading = false;
                }
            }
        }
    }
};
</script>
